import { styled } from '@mui/material/styles';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  FormControl,
  Button,
  Chip,
  Grid,
} from '@mui/material';
import { colors, spacing } from '../../../styles/theme';

// Drawer principal
export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: { xs: '100%', sm: '75%' },
    maxWidth: '768px',
  },
}));

// Container principal
export const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: colors.grey[50],
}));

// Header del drawer
export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${colors.grey[200]}`,
  padding: spacing.lg,
  backgroundColor: colors.background.paper,
}));

// Título del header
export const DrawerTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: colors.text.primary,
}));

// Botón de cerrar
export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: colors.text.secondary,
  '&:hover': {
    backgroundColor: colors.grey[100],
  },
}));

// Barra de progreso
export const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: `${spacing.md} ${spacing.lg}`,
  backgroundColor: colors.background.paper,
}));

// Barra de progreso
export const ProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: colors.grey[200],
  borderRadius: spacing.xs,
  height: 8,
  overflow: 'hidden',
}));

// Barra de progreso activa
export const ProgressBarFill = styled(Box)(({ theme }) => ({
  height: '100%',
  borderRadius: spacing.xs,
  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.info.main} 25%, ${colors.success.main} 50%, ${colors.warning.main} 100%)`,
  transition: 'width 0.5s ease-in-out',
}));

// Información de progreso
export const ProgressInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '14px',
  color: colors.text.secondary,
  marginTop: spacing.sm,
}));

// Container del indicador de pasos
export const StepIndicatorContainer = styled(Box)(({ theme }) => ({
  padding: spacing.lg,
  backgroundColor: colors.background.paper,
}));

// Indicador de pasos
export const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: spacing.xl,
  padding: `0 ${spacing.sm}`,
  marginTop: spacing.sm,
}));

// Item del indicador de pasos
export const StepItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
}));

// Círculo del paso
export const StepCircle = styled(Box)<{
  isActive: boolean;
  isCompleted: boolean;
  stepColor: string;
}>(({ theme, isActive, isCompleted, stepColor }) => ({
  width: 64,
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: 'all 0.3s ease',
  transform: isActive ? 'scale(1.15)' : 'scale(1)',
  color: isActive || isCompleted ? colors.background.paper : colors.text.secondary,
  '& .step-circle-bg': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: isActive || isCompleted ? stepColor : colors.grey[300],
    filter: isActive || isCompleted ? 'drop-shadow(0px 3px 5px rgba(0,0,0,0.25))' : 'none',
  },
  '& .step-icon': {
    position: 'relative',
    zIndex: 10,
    transition: 'all 0.3s ease',
  },
}));

// Línea conectora entre pasos
export const StepConnector = styled(Box)<{
  isCompleted: boolean;
  fromColor: string;
  toColor: string;
}>(({ theme, isCompleted, fromColor, toColor }) => ({
  position: 'absolute',
  top: 32,
  left: '50%',
  width: '100%',
  height: 6,
  zIndex: -2,
  background: isCompleted
    ? `linear-gradient(to right, ${fromColor}, ${toColor})`
    : colors.grey[300],
}));

// Etiqueta del paso
export const StepLabel = styled(Typography)<{ isActive: boolean; isCompleted: boolean }>(
  ({ theme, isActive, isCompleted }) => ({
    fontSize: '14px',
    fontWeight: 500,
    marginTop: spacing.md,
    color: isActive || isCompleted ? colors.text.primary : colors.text.secondary,
  }),
);

// Container del contenido
export const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: spacing.lg,
  overflowY: 'auto',
}));

// Card de paso
export const StepCard = styled(Box)(({ theme }) => ({
  backgroundColor: colors.background.paper,
  borderRadius: spacing.lg,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  marginBottom: spacing.xl,
}));

// Header del card
export const StepCardHeader = styled(Box)<{ stepColor: string }>(({ theme, stepColor }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: spacing.lg,
  backgroundColor: stepColor + '10',
  borderLeft: `4px solid ${stepColor}`,
}));

// Título del card
export const StepCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 500,
  color: colors.text.primary,
  marginLeft: spacing.md,
}));

// Contenido del card
export const StepCardContent = styled(Box)(({ theme }) => ({
  padding: spacing.xl,
  borderTop: `1px solid ${colors.grey[200]}`,
}));

// Grid de campos
export const FieldsGrid = styled(Grid)(({ theme }) => ({
  gap: spacing.xl,
  marginBottom: spacing.xl,
  '&:last-child': {
    marginBottom: 0,
  },
}));

// Label de campo
export const FieldLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  color: colors.text.primary,
  marginBottom: spacing.xs,
}));

// TextField estilizado
export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: spacing.sm,
  },
}));

// FormControl estilizado
export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: spacing.sm,
  },
}));

// Container de tags
export const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: spacing.sm,
  marginBottom: spacing.md,
}));

// Tag individual
export const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: colors.info.light + '20',
  color: colors.info.main,
  '& .MuiChip-deleteIcon': {
    color: colors.info.main,
  },
}));

// Input de nueva tag
export const TagInput = styled('input')(({ theme }) => ({
  flex: 1,
  padding: spacing.sm,
  border: `1px solid ${colors.grey[300]}`,
  borderRadius: spacing.sm,
  transition: 'all 0.2s ease',
  '&:focus': {
    outline: 'none',
    borderColor: colors.primary.main,
    boxShadow: `0 0 0 2px ${colors.primary.light}20`,
  },
}));

// Footer del drawer
export const DrawerFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${colors.grey[200]}`,
  padding: spacing.lg,
  backgroundColor: colors.background.paper,
}));

// Container de botones
export const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// Container de botones de la derecha
export const RightButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: spacing.md,
}));

// Botón anterior
export const PreviousButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  borderColor: colors.grey[300],
  color: colors.text.primary,
  '&:hover': {
    backgroundColor: colors.grey[50],
  },
}));

// Botón siguiente
export const NextButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.success.main} 100%)`,
  color: colors.background.paper,
  '&:hover': {
    background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.success.dark} 100%)`,
  },
}));

// Botón guardar
export const SaveButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.success.main} 100%)`,
  color: colors.background.paper,
  '&:hover': {
    background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.success.dark} 100%)`,
  },
}));

// Modal de confirmación
export const ConfirmationModal = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
}));

// Card del modal
export const ConfirmationCard = styled(Box)(({ theme }) => ({
  backgroundColor: colors.background.paper,
  borderRadius: spacing.lg,
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  padding: spacing.xl,
  maxWidth: '400px',
  width: '100%',
  margin: spacing.lg,
}));

// Header del modal
export const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: spacing.lg,
  color: colors.primary.main,
}));

// Título del modal
export const ModalTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 500,
  color: colors.text.primary,
  marginLeft: spacing.md,
}));

// Mensaje del modal
export const ModalMessage = styled(Typography)(({ theme }) => ({
  color: colors.text.secondary,
  marginBottom: spacing.xl,
}));

// Container de botones del modal
export const ModalButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacing.md,
}));

// Botón de cancelar del modal
export const ModalCancelButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  backgroundColor: colors.background.paper,
  color: colors.text.primary,
  border: `1px solid ${colors.grey[300]}`,
  borderRadius: spacing.sm,
  '&:hover': {
    backgroundColor: colors.grey[50],
  },
}));

// Botón de confirmar del modal
export const ModalConfirmButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.success.main} 100%)`,
  color: colors.background.paper,
  borderRadius: spacing.sm,
  '&:hover': {
    background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.success.dark} 100%)`,
  },
}));
